<template>
  <div class="companies-container">
    <div class="container-padded py-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0 animate-fade-in display-heading">
          <span class="icon icon-primary icon-circle me-2"><i class="fas fa-building"></i></span> Neue Firma erstellen
        </h1>
        <router-link to="/companies" class="btn btn-outline-secondary btn-with-icon animate-fade-in with-hover-effect">
          <span class="icon me-2"><i class="fas fa-arrow-left"></i></span> Zurück zur Firmenliste
        </router-link>
      </div>
      
      <div class="row">
        <div class="col-12">
          <Card animateIn="fade-in-up" class="card-with-form">
            <form @submit.prevent="saveCompany" class="form-spaced">
              <div class="mb-4">
                <label for="companyName" class="form-label label-emphasized required">Firmenname</label>
                <input 
                  type="text" 
                  class="form-control form-control-lg with-focus-effect" 
                  id="companyName" 
                  v-model="company.name" 
                  placeholder="Firmenname"
                  required
                  :class="{ 'is-invalid': errors.name }"
                >
                <div class="invalid-feedback" v-if="errors.name">
                  <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                  {{ errors.name }}
                </div>
              </div>
              
              <div class="mb-4">
                <label for="companyEmail" class="form-label label-emphasized required">E-Mail</label>
                <input 
                  type="email" 
                  class="form-control with-focus-effect" 
                  id="companyEmail" 
                  v-model="company.email" 
                  placeholder="firma@beispiel.de"
                  required
                  :class="{ 'is-invalid': errors.email }"
                >
                <div class="invalid-feedback" v-if="errors.email">
                  <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                  {{ errors.email }}
                </div>
              </div>
              
              <div class="mb-4">
                <label for="companyWebsite" class="form-label label-emphasized">Webseite</label>
                <input 
                  type="url" 
                  class="form-control with-focus-effect" 
                  id="companyWebsite" 
                  v-model="company.website" 
                  placeholder="https://www.beispiel.de"
                  :class="{ 'is-invalid': errors.website }"
                >
                <div class="invalid-feedback" v-if="errors.website">
                  <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                  {{ errors.website }}
                </div>
              </div>
              
              <div class="row mb-4">
                <div class="col-md-6">
                  <label for="companyStreet" class="form-label label-emphasized">Straße</label>
                  <input 
                    type="text" 
                    class="form-control with-focus-effect" 
                    id="companyStreet" 
                    v-model="company.street" 
                    placeholder="Straße und Hausnummer"
                    :class="{ 'is-invalid': errors.street }"
                  >
                  <div class="invalid-feedback" v-if="errors.street">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ errors.street }}
                  </div>
                </div>
                <div class="col-md-6">
                  <label for="companyZip" class="form-label label-emphasized">PLZ</label>
                  <input 
                    type="text" 
                    class="form-control with-focus-effect" 
                    id="companyZip" 
                    v-model="company.zip" 
                    placeholder="Postleitzahl"
                    :class="{ 'is-invalid': errors.zip }"
                  >
                  <div class="invalid-feedback" v-if="errors.zip">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ errors.zip }}
                  </div>
                </div>
              </div>
              
              <div class="row mb-4">
                <div class="col-md-6">
                  <label for="companyCity" class="form-label label-emphasized">Ort</label>
                  <input 
                    type="text" 
                    class="form-control with-focus-effect" 
                    id="companyCity" 
                    v-model="company.city" 
                    placeholder="Ort"
                    :class="{ 'is-invalid': errors.city }"
                  >
                  <div class="invalid-feedback" v-if="errors.city">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ errors.city }}
                  </div>
                </div>
                <div class="col-md-6">
                  <label for="companyPhone" class="form-label label-emphasized">Telefonnummer</label>
                  <input 
                    type="tel" 
                    class="form-control with-focus-effect" 
                    id="companyPhone" 
                    v-model="company.phone" 
                    placeholder="z.B. +49 123 456789"
                    :class="{ 'is-invalid': errors.phone }"
                  >
                  <div class="invalid-feedback" v-if="errors.phone">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ errors.phone }}
                  </div>
                </div>
              </div>
              
              <div class="mb-4">
                <label for="companyColor" class="form-label label-emphasized">Hauptfarbe</label>
                <div class="input-group">
                  <span class="input-group-text color-preview" :style="{ backgroundColor: company.primary_color }"></span>
                  <input 
                    type="color" 
                    class="form-control form-control-color" 
                    id="companyColor" 
                    v-model="company.primary_color" 
                    title="Hauptfarbe auswählen"
                    :class="{ 'is-invalid': errors.primary_color }"
                  >
                  <input 
                    type="text" 
                    class="form-control with-focus-effect" 
                    v-model="company.primary_color" 
                    placeholder="#0071e3"
                    :class="{ 'is-invalid': errors.primary_color }"
                  >
                  <div class="invalid-feedback" v-if="errors.primary_color">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ errors.primary_color }}
                  </div>
                </div>
              </div>
              
              <div class="mb-4">
                <label for="companyLogo" class="form-label label-emphasized">Logo</label>
                <div class="custom-file-upload">
                  <input 
                    type="file" 
                    class="custom-file-input" 
                    id="companyLogo" 
                    @change="handleLogoUpload"
                    accept="image/*"
                    :class="{ 'is-invalid': errors.logo }"
                    ref="logoInput"
                  >
                  <label class="custom-file-label" for="companyLogo">
                    <span class="icon me-2"><i class="fas fa-upload"></i></span>
                    <span>
                      <span v-if="company.logo || logoPreview">{{ logoFileName || 'Logo ausgewählt' }}</span>
                      <span v-else>Keine ausgewählt</span>
                    </span>
                  </label>
                </div>
                <div class="invalid-feedback" v-if="errors.logo">
                  <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                  {{ errors.logo }}
                </div>
                <div class="d-flex align-items-center mt-2" v-if="logoPreview || company.logo">
                  <div class="preview-container me-3">
                    <img :src="logoPreview" alt="Logo Vorschau" class="img-thumbnail logo-preview">
                  </div>
                  <button 
                    type="button" 
                    class="btn btn-outline-danger btn-sm"
                    @click="removeLogo"
                  >
                    <span class="icon me-1"><i class="fas fa-trash-alt"></i></span> 
                    Entfernen
                  </button>
                </div>
              </div>
              
              <div class="mb-4">
                <h3 class="mb-3">Administrator-Informationen</h3>
                <p class="text-muted mb-4">Bitte geben Sie die Informationen für den ersten Administrator dieser Firma ein.</p>
                
                <div class="mb-4">
                  <label for="adminName" class="form-label label-emphasized required">Admin Name</label>
                  <input 
                    type="text" 
                    class="form-control with-focus-effect" 
                    id="adminName" 
                    v-model="company.admin_name" 
                    placeholder="Name des Administrators"
                    required
                    :class="{ 'is-invalid': errors.admin_name }"
                  >
                  <div class="invalid-feedback" v-if="errors.admin_name">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ errors.admin_name }}
                  </div>
                </div>
                
                <div class="mb-4">
                  <label for="adminEmail" class="form-label label-emphasized required">Admin E-Mail</label>
                  <input 
                    type="email" 
                    class="form-control with-focus-effect" 
                    id="adminEmail" 
                    v-model="company.admin_email" 
                    placeholder="E-Mail des Administrators"
                    required
                    :class="{ 'is-invalid': errors.admin_email }"
                  >
                  <div class="invalid-feedback" v-if="errors.admin_email">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ errors.admin_email }}
                  </div>
                </div>
                
                <div class="mb-4">
                  <label for="adminPassword" class="form-label label-emphasized required">Admin Passwort</label>
                  <input 
                    type="password" 
                    class="form-control with-focus-effect" 
                    id="adminPassword" 
                    v-model="company.admin_password" 
                    placeholder="Passwort des Administrators"
                    required
                    :class="{ 'is-invalid': errors.admin_password }"
                  >
                  <div class="form-text">Sicheres Passwort für den Administrator-Account.</div>
                  <div class="invalid-feedback" v-if="errors.admin_password">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ errors.admin_password }}
                  </div>
                </div>
              </div>
              
              <div class="d-flex justify-content-between mt-5">
                <router-link to="/companies" class="btn btn-outline-secondary">Abbrechen</router-link>
                <button type="submit" class="btn btn-primary btn-lg" :disabled="loading">
                  <span class="spinner-border spinner-border-sm me-2" v-if="loading" role="status"></span>
                  {{ loading ? 'Wird gespeichert...' : 'Firma speichern' }}
                </button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Card from '@/components/ui/Card.vue';

export default {
  name: 'CompanyCreate',
  components: {
    Card
  },
  data() {
    return {
      loading: false,
      company: {
        name: '',
        email: '',
        website: '',
        street: '',
        zip: '',
        city: '',
        phone: '',
        primary_color: '#0071e3',
        logo: null,
        admin_name: '',
        admin_email: '',
        admin_password: ''
      },
      logoPreview: null,
      logoFileName: '',
      errors: {}
    };
  },
  computed: {
    isNewCompany() {
      return !this.$route.params.id;
    },
    companyId() {
      return this.$route.params.id;
    },
    displayLogoName() {
      if (this.logoFileName) {
        return this.logoFileName;
      }
      
      if (this.logoPreview || this.company.logo) {
        return 'Logo vorhanden';
      }
      
      return 'Keine ausgewählt';
    }
  },
  methods: {
    ...mapActions({
      createCompanyAction: 'companies/createCompany',
      updateCompanyAction: 'companies/updateCompany',
      fetchCompanyAction: 'companies/fetchCompany'
    }),
    async saveCompany() {
      this.loading = true;
      this.errors = {};
      
      try {
        const formData = new FormData();
        Object.keys(this.company).forEach(key => {
          if (key === 'logo' && this.company[key] instanceof File) {
            formData.append(key, this.company[key]);
          } else if (this.company[key] !== null && this.company[key] !== undefined) {
            formData.append(key, this.company[key]);
          }
        });
        
        if (!this.company.logo && !this.isNewCompany) {
          formData.append('remove_logo', '1');
        }
        
        if (this.isNewCompany) {
          const createdCompany = await this.createCompanyAction(formData);
          this.$router.push('/companies');
          this.$toast.success('Firma wurde erfolgreich erstellt.');
        } else {
          await this.updateCompanyAction({
            id: this.companyId,
            companyData: formData
          });
          this.$toast.success('Firma wurde erfolgreich aktualisiert.');
        }
      } catch (error) {
        this.$toast.error('Fehler beim Speichern der Firma');
        console.error(error);
        
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        }
      } finally {
        this.loading = false;
      }
    },
    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      this.company.logo = file;
      this.logoFileName = file.name;
      console.log('Hochgeladenes Logo:', file.name);
      
      const reader = new FileReader();
      reader.onload = e => {
        this.logoPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    removeLogo() {
      this.company.logo = null;
      this.logoFileName = '';
      this.logoPreview = null;
      if (this.$refs.logoInput) {
        this.$refs.logoInput.value = '';
      }
    },
    async loadCompany() {
      if (!this.companyId) return;
      
      this.loading = true;
      try {
        const company = await this.fetchCompanyAction(this.companyId);
        console.log('Geladene Firmendaten:', company);
        
        this.company = {
          ...company,
          password: ''
        };
        
        if (company.logo_url) {
          this.logoPreview = company.logo_url;
          console.log('Logo URL:', company.logo_url);
          
          // Extrahiere den Dateinamen aus der URL
          try {
            const url = new URL(company.logo_url);
            
            // Prüfe, ob der originalName-Parameter in der URL vorhanden ist
            if (url.searchParams.has('originalName')) {
              // Verwende den originalName-Parameter direkt
              this.logoFileName = url.searchParams.get('originalName');
              console.log('Originaler Dateiname aus URL-Parameter:', this.logoFileName);
            } else {
              // Fallback zur Pfad-Extraktion
              const pathParts = url.pathname.split('/');
              const encodedFilename = pathParts[pathParts.length - 1];
              
              // Versuche verschiedene Dekodierungsmethoden
              try {
                // Methode 1: Standard URL-Dekodierung
                this.logoFileName = decodeURIComponent(encodedFilename);
              } catch (e) {
                console.warn("Standard-Dekodierung fehlgeschlagen, versuche alternative Methoden");
                
                // Methode 2: Manuelles Ersetzen von %20 etc.
                this.logoFileName = encodedFilename.replace(/%([0-9A-F]{2})/g, (match, p1) => {
                  return String.fromCharCode(parseInt(p1, 16));
                });
              }
              
              // Bereinige den Dateinamen von möglichen Hash-Präfixen
              if (this.logoFileName.includes('_')) {
                const parts = this.logoFileName.split('_');
                if (parts.length > 1 && parts[0].length > 20) {
                  // Wenn der erste Teil wie ein Hash aussieht, entferne ihn
                  this.logoFileName = parts.slice(1).join('_');
                }
              }
              
              console.log('Extrahierter Dateiname aus Pfad:', this.logoFileName);
            }
          } catch (e) {
            console.error('Fehler beim Extrahieren des Dateinamens:', e);
            // Letzter Ausweg: Verwende den letzten Teil des Pfads ohne Dekodierung
            const parts = company.logo_url.split('/');
            this.logoFileName = parts[parts.length - 1].split('?')[0]; // Entferne Query-Parameter
          }
        } else {
          this.logoPreview = null;
          this.logoFileName = '';
          console.log('Kein Logo vorhanden');
        }
      } catch (error) {
        this.$toast.error('Fehler beim Laden der Firmendaten');
        console.error(error);
      } finally {
        this.loading = false;
      }
    }
  },
  created() {
    console.log('Initial logoFileName:', this.logoFileName);
    console.log('Initial company.logo:', this.company.logo);
    if (this.companyId) {
      this.loadCompany();
    }
  }
};
</script>

<style lang="scss" scoped>
.company-create {
  .card {
    border-radius: var(--border-radius);
    overflow: hidden;
  }
  
  .form-label {
    margin-bottom: 0.5rem;
  }
  
  .form-control, .form-select {
    padding: var(--input-padding-y) var(--input-padding-x);
    
    &:focus {
      border-color: var(--primary-color);
      box-shadow: 0 0 0 0.25rem rgba(var(--primary-color), 0.25);
    }
  }
  
  .color-picker {
    .color-preview {
      width: 20px;
      height: 20px;
      border-radius: 4px;
      display: inline-block;
    }
  }
  
  .btn-primary {
    background-color: var(--primary-color);
    border-color: var(--primary-color);
    
    &:hover {
      background-color: var(--primary-color-dark);
      border-color: var(--primary-color-dark);
    }
  }
}

.color-preview {
  width: 40px;
}

.logo-preview {
  max-height: 150px;
  max-width: 300px;
}

.custom-file-upload {
  position: relative;
  
  .custom-file-input {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
  
  .custom-file-label {
    display: flex;
    align-items: center;
    padding: var(--input-padding-y) var(--input-padding-x);
    border: 1px solid var(--input-border-color);
    border-radius: var(--border-radius);
    background-color: var(--card-bg);
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      border-color: var(--primary-color);
    }
  }
}

.form-spaced {
  .form-label {
    font-weight: 500;
    margin-bottom: 0.5rem;
    
    &.required::after {
      content: "*";
      color: var(--danger-color);
      margin-left: 0.25rem;
    }
  }
}
</style> 